<template lang="html">
  <div class="trade-settings">
    <div class="section-header">
      <h2>Trades</h2>

      <button
        v-if="isCommissioner && !editing"
        class="small edit"
        @click="addTrade"
      >
        Add trade
      </button>

      <div class="actions" v-if="editing">
        <a @click="cancel">Cancel</a>

        <button class="small edit" @click="saveTrade" :disabled="!enableSave">
          Save
        </button>
      </div>
    </div>

    <section class="editing" v-if="editing">
      <div class="add-trade">
        <section class="trade-team">
          <div class="input-group">
            <div>
              <h4>Team 1</h4>
              <multiselect
                v-model="team1.team"
                :options="team1Options"
                label="label"
                placeholder="Team 1"
              />
            </div>

            <div>
              <h4>Sends</h4>
              <multiselect
                v-model="team1.round"
                :options="round1Options"
                label="label"
                placeholder="Round"
              />
            </div>
          </div>
        </section>

        <section class="trade-team">
          <div class="input-group">
            <div>
              <h4>Team 2</h4>
              <multiselect
                v-model="team2.team"
                :options="team2Options"
                label="label"
                placeholder="Team 2"
              />
            </div>

            <div>
              <h4>Sends</h4>
              <multiselect
                v-model="team2.round"
                :options="round2Options"
                label="label"
                placeholder="Round"
              />
            </div>
          </div>
        </section>
      </div>
    </section>

    <section class="display">
      <div class="trade" v-for="(trade, i) in trades" :key="i">
        <div class="trade-description" v-html="tradeDescription(trade)" />

        <div
          v-if="isCommissioner"
          class="trade-trash"
          @click="tryRemoveTrade(i)"
        >
          <i class="fas fa-trash" />
        </div>
      </div>

      <p class="subtle" v-show="!draft.trades || draft.trades.length === 0">
        This draft has no trades yet.
      </p>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import clonedeep from 'lodash.clonedeep';

export default {
  data() {
    return {
      trades: [],
      editing: false,
      team1: { team: {}, round: {} },
      team2: { team: {}, round: {} },
    };
  },

  created() {
    this.trades = clonedeep(this.draft.trades || []);
  },

  watch: {
    'draft.trades': {
      handler(value) {
        this.trades = clonedeep(value || []);
      },
      deep: true,
    },
  },

  computed: {
    ...mapState({
      draft: ({ draft }) => draft.draft,
    }),

    ...mapGetters({
      isCommissioner: 'draft/isCommissioner',
    }),

    teamOptions() {
      return this.draft.teams
        .map(({ name, id }) => {
          return {
            value: id,
            label: name,
            id,
          };
        });
    },

    team1Options() {
      return this.teamOptions.filter(({ value }) => value !== this.team2.team.value);
    },

    team2Options() {
      return this.teamOptions.filter(({ value }) => value !== this.team1.team.value);
    },

    round1Options() {
      if (this.team1.team.value === undefined) {
        return [];
      }

      return this.$store.getters['draft/tradeKeeperRoundOptions'](this.team1.team);
    },

    round2Options() {
      if (this.team2.team.value === undefined) {
        return [];
      }

      return this.$store.getters['draft/tradeKeeperRoundOptions'](this.team2.team);
    },

    enableSave() {
      return this.team1.team.value !== undefined &&
        this.team1.round.value !== undefined &&
        this.team2.team.value !== undefined &&
        this.team2.round.value !== undefined;
    },
  },

  methods: {
    addTrade() {
      this.editing = true;
    },

    cancel() {
      this.team1 = { team: {}, round: {} };
      this.team2 = { team: {}, round: {} };
      this.editing = false;
    },

    saveTrade() {
      this.trades.push({
        t1: this.team1.team.value,
        t2: this.team2.team.value,
        r1: this.team1.round.value,
        r2: this.team2.round.value,
      });

      this.save();
    },

    save() {
      this.$store.dispatch('draft/SAVE_TRADES', this.trades)
        .then(() => {
          this.$toast({ message: 'Saved your trades' });
          this.cancel();
        });
    },

    tryRemoveTrade(index) {
      this.$confirm({ title: 'Remove trade', message: 'Are you sure you want to remove this trade from the draft?' })
        .then(() => {
          this.removeTrade(index);
        })
        .catch(() => {});
    },

    removeTrade(index) {
      this.trades.splice(index, 1);

      this.save();
    },

    tradeDescription(trade) {
      const team1 = this.draft.teams.find(({ id }) => id === trade.t1);
      const team2 = this.draft.teams.find(({ id }) => id === trade.t2);

      if (!team1 || !team2) {
        return '';
      }

      return `${team1.name} trades round ${trade.r1} and receives round ${trade.r2} <br />
        ${team2.name} trades round ${trade.r2} and receives round ${trade.r1}`;
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import "./settings";

  .trade-settings {
    @include settingSection;
  }

  .trade {
    display: flex;
    align-items: center;
    padding: $gp / 2 0;

    &:not(:last-of-type) {
      border-bottom: 1px solid $gray-color;
    }
  }

  .add-trade {
    margin: $gp * 2 0;
  }

  .trade-trash {
    width: px-to-rem(50px);
    margin-left: $gp;
  }

  .trade-team {
    margin-bottom: $gp;
  }

  .editing {
    .trade {
      border-bottom: none;
    }

    .team-email {
      margin-bottom: $gp;
    }

    @media($small) {
      .trade {
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: $gp * 2;
      }
    }
  }
</style>
